import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { APP_TZ } from "../../lib/format";
import type { ChangeOrderDecider, ChangeOrderStatus } from "../change-order/types";
import { CUSTOMER_SOURCES, type CustomerSource } from "../customer/types";
import { sourceLabel } from "./source-funnel";

dayjs.extend(utc);
dayjs.extend(timezone);

// 월간 리포트(/app/insights/report?month=YYYY-MM) — 저장소는 달 범위로 좁힌 날것만 넘기고 세기는 여기 하나가 한다.

export const REPORT_PATH = "/app/insights/report";
// 달 칩 개수 — 이번 달부터 거슬러.
export const REPORT_MONTH_CHIPS = 6;

const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

export interface ReportRange {
  // KST `YYYY-MM`.
  month: string;
  from: string;
  to: string;
}

export interface ReportChangeOrderFact {
  id: string;
  status: ChangeOrderStatus;
  decided_by: ChangeOrderDecider | null;
  decided_at: string | null;
  delta_total: number;
}

export interface ReportViewFact {
  id: string;
  viewed_at: string | null;
}

export interface ReportCustomerFact {
  id: string;
  source: CustomerSource | null;
  created_at: string;
}

export interface ReportContractFact {
  id: string;
  signed_at: string | null;
  replaces_contract_id: string | null;
}

export interface ReportCreatedFact {
  id: string;
  created_at: string;
}

export interface ReportPortfolioFact extends ReportCreatedFact {
  published: boolean;
}

// 저장소가 넘기는 날것 — 범위는 좁히기용일 뿐이고 달·상태 거르기는 buildMonthlyReport 가 다시 한다.
export interface ReportFacts {
  changeOrders: ReportChangeOrderFact[];
  quoteShares: ReportViewFact[];
  contractShares: ReportViewFact[];
  customers: ReportCustomerFact[];
  contracts: ReportContractFact[];
  logs: ReportCreatedFact[];
  photos: ReportCreatedFact[];
  portfolios: ReportPortfolioFact[];
}

export const emptyReportFacts = (): ReportFacts => ({
  changeOrders: [],
  quoteShares: [],
  contractShares: [],
  customers: [],
  contracts: [],
  logs: [],
  photos: [],
  portfolios: [],
});

export interface ReportSourceCount {
  source: CustomerSource | null;
  label: string;
  count: number;
}

export interface MonthlyReport {
  month: string;
  changeOrders: { amount: number; count: number; byLink: number; byStaff: number };
  // 열람마다 시각이 남는 기록이 없어 횟수는 「집계 안 됨」 — 그 달 처음 열린 링크 수만 센다.
  linkViews: { tracked: false; quoteFirstViews: number; contractFirstViews: number };
  inquiries: { total: number; bySource: ReportSourceCount[] };
  contracts: number;
  logs: number;
  photos: number;
  portfolios: { created: number; published: number };
}

const kstNow = (now: Date | string) => dayjs(now).tz(APP_TZ);

export const currentReportMonth = (now: Date | string): string => kstNow(now).format("YYYY-MM");

export const defaultReportMonth = (now: Date | string): string => kstNow(now).startOf("month").subtract(1, "month").format("YYYY-MM");

// `?month=` — 형식이 맞고 이번 달(KST)을 넘지 않을 때만 받고, 아니면 지난달.
export const parseReportMonth = (value: unknown, now: Date | string): string => {
  if (typeof value !== "string" || !MONTH_RE.test(value)) return defaultReportMonth(now);
  return value <= currentReportMonth(now) ? value : defaultReportMonth(now);
};

// KST 달 첫날 0시 ~ 다음 달 첫날 0시를 UTC ISO 로.
export const reportRangeOf = (month: string): ReportRange => {
  const start = dayjs.tz(`${month}-01 00:00`, APP_TZ);
  return { month, from: start.toISOString(), to: start.add(1, "month").toISOString() };
};

export const reportHref = (month: string, now: Date | string): string =>
  month === defaultReportMonth(now) ? REPORT_PATH : `${REPORT_PATH}?month=${month}`;

// 달 칩 — 이번 달부터 거슬러 count 개(최근 달이 앞).
export const reportMonthOptions = (now: Date | string, count: number = REPORT_MONTH_CHIPS): string[] => {
  const start = kstNow(now).startOf("month");
  return Array.from({ length: count }, (_, i) => start.subtract(i, "month").format("YYYY-MM"));
};

// 칩·머리 표기 — 같은 해면 「9월」, 해가 다르면 「2025년 12월」.
export const reportMonthLabel = (month: string, now: Date | string): string => {
  const [, y, m] = MONTH_RE.exec(month) ?? [];
  if (!y || !m) return month;
  return y === currentReportMonth(now).slice(0, 4) ? `${Number(m)}월` : `${y}년 ${Number(m)}월`;
};

const inRange = (iso: string | null, from: number, to: number): boolean => {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t >= from && t < to;
};

const sourceOrder = (source: CustomerSource | null): number =>
  source === null ? CUSTOMER_SOURCES.length : CUSTOMER_SOURCES.indexOf(source);

export const buildMonthlyReport = (facts: ReportFacts, range: ReportRange): MonthlyReport => {
  const from = Date.parse(range.from);
  const to = Date.parse(range.to);
  const within = (iso: string | null) => inRange(iso, from, to);

  const accepted = facts.changeOrders.filter((c) => c.status === "ACCEPTED" && within(c.decided_at));

  const customers = facts.customers.filter((c) => within(c.created_at));
  const bySource = new Map<CustomerSource | null, number>();
  for (const c of customers) bySource.set(c.source, (bySource.get(c.source) ?? 0) + 1);

  const portfolios = facts.portfolios.filter((p) => within(p.created_at));

  return {
    month: range.month,
    changeOrders: {
      amount: accepted.reduce((sum, c) => sum + (Number(c.delta_total) || 0), 0),
      count: accepted.length,
      byLink: accepted.filter((c) => c.decided_by === "CUSTOMER").length,
      byStaff: accepted.filter((c) => c.decided_by !== "CUSTOMER").length,
    },
    linkViews: {
      tracked: false,
      quoteFirstViews: facts.quoteShares.filter((s) => within(s.viewed_at)).length,
      contractFirstViews: facts.contractShares.filter((s) => within(s.viewed_at)).length,
    },
    inquiries: {
      total: customers.length,
      bySource: Array.from(bySource.entries())
        .map(([source, count]) => ({ source, label: sourceLabel(source), count }))
        .sort((a, b) => b.count - a.count || sourceOrder(a.source) - sourceOrder(b.source)),
    },
    contracts: facts.contracts.filter((c) => c.replaces_contract_id === null && within(c.signed_at)).length,
    logs: facts.logs.filter((l) => within(l.created_at)).length,
    photos: facts.photos.filter((p) => within(p.created_at)).length,
    portfolios: { created: portfolios.length, published: portfolios.filter((p) => p.published).length },
  };
};
